/**
 * People analytics — headcount, joiners and leavers, attrition, tenure and exits, as at any date.
 *
 * Read-only. Every number is derived from employments and the assignment in effect on the day being
 * asked about (CLAUDE.md invariant 5), so "headcount by department in March" is right even after
 * people moved. Nothing here is salary and nothing is personal data: no compensation, no date of
 * birth (encrypted; ages are deliberately not offered), no names. Counts only.
 *
 * Who may call: anyone with `employee:read`, but each caller only counts the people they may read
 * (an entity-scoped HR admin sees their entity, a department head their department). A caller whose
 * read is limited to themselves or their team gets a 403: team analytics is a later addition.
 * Exporting needs `employee:export` as well.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { dateOnlySchema, errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

/** What the headcount can be split by. Each is the value in effect on the `asOf` day. */
export const HEADCOUNT_GROUPINGS = [
  'department',
  'location',
  'legalEntity',
  'grade',
  'designation',
  'employmentType',
  'workType',
  'gender',
  'costCentre',
  'businessUnit',
] as const;
export const headcountGroupingSchema = z.enum(HEADCOUNT_GROUPINGS);
export type HeadcountGrouping = z.infer<typeof headcountGroupingSchema>;

/** The filters every report takes. Omit all for the whole of what the caller may read, today. */
export const peopleAnalyticsFilterSchema = z.object({
  /** The day to report on; defaults to today in the organisation's head-office timezone. */
  asOf: dateOnlySchema.optional(),
  legalEntityId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
});
export type PeopleAnalyticsFilter = z.infer<typeof peopleAnalyticsFilterSchema>;

export const peopleAnalyticsSummarySchema = z.object({
  asOf: dateOnlySchema,
  headcount: z.number().int().nonnegative(),
  /** Joined / left in the 30 days up to and including `asOf`. */
  joinersLast30Days: z.number().int().nonnegative(),
  leaversLast30Days: z.number().int().nonnegative(),
  /**
   * People who moved in from / out to another legal entity in the same 30 days. A transfer is neither
   * a hire nor a leaver. Optional so older clients keep parsing; always sent.
   */
  transfersInLast30Days: z.number().int().nonnegative().optional(),
  transfersOutLast30Days: z.number().int().nonnegative().optional(),
  /** Leavers over the last 12 months as a percentage of average headcount; null with no headcount. */
  attritionPercentLast12Months: z.number().nullable(),
  /** Years of service, one decimal; null with no headcount. */
  averageTenureYears: z.number().nullable(),
  onNotice: z.number().int().nonnegative(),
  onProbation: z.number().int().nonnegative(),
  /** Fixed-term contracts that end within 30, 60 and 90 days (cumulative: 30 is inside 60). */
  contractsEnding: z.object({
    within30Days: z.number().int().nonnegative(),
    within60Days: z.number().int().nonnegative(),
    within90Days: z.number().int().nonnegative(),
  }),
});
export type PeopleAnalyticsSummary = z.infer<typeof peopleAnalyticsSummarySchema>;

export const headcountBucketSchema = z.object({
  /** Id of the department, location and so on; the plain value for text groupings; null for "not set". */
  key: z.string().nullable(),
  label: z.string(),
  count: z.number().int().nonnegative(),
  /** Share of the total, one decimal. */
  percent: z.number(),
});
export type HeadcountBucket = z.infer<typeof headcountBucketSchema>;

export const headcountReportSchema = z.object({
  asOf: dateOnlySchema,
  groupBy: headcountGroupingSchema,
  total: z.number().int().nonnegative(),
  /** Largest first; "Not set" is last whatever its size. */
  buckets: z.array(headcountBucketSchema),
});
export type HeadcountReport = z.infer<typeof headcountReportSchema>;

export const trendPointSchema = z.object({
  /** `YYYY-MM`. */
  month: z.string(),
  opening: z.number().int().nonnegative(),
  joiners: z.number().int().nonnegative(),
  leavers: z.number().int().nonnegative(),
  /** Transfers into / out of the scope that month (neither hires nor leavers). */
  transfersIn: z.number().int().nonnegative().optional(),
  transfersOut: z.number().int().nonnegative().optional(),
  closing: z.number().int().nonnegative(),
  attritionPercent: z.number().nullable(),
});
export type TrendPoint = z.infer<typeof trendPointSchema>;

export const trendReportSchema = z.object({
  asOf: dateOnlySchema,
  months: z.number().int(),
  points: z.array(trendPointSchema),
});
export type TrendReport = z.infer<typeof trendReportSchema>;

export const exitBucketSchema = z.object({
  key: z.string().nullable(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});

export const exitsReportSchema = z.object({
  /** The 12 months up to and including `asOf`. */
  from: dateOnlySchema,
  to: dateOnlySchema,
  total: z.number().int().nonnegative(),
  byType: z.array(exitBucketSchema),
  byReason: z.array(exitBucketSchema),
  /** How long leavers had been with the company when they left. */
  tenureAtExit: z.array(exitBucketSchema),
});
export type ExitsReport = z.infer<typeof exitsReportSchema>;

export const tenureReportSchema = z.object({
  asOf: dateOnlySchema,
  bands: z.array(z.object({ band: z.string(), label: z.string(), count: z.number().int().nonnegative() })),
});
export type TenureReport = z.infer<typeof tenureReportSchema>;

export const PEOPLE_ANALYTICS_EXPORTS = ['headcount', 'trend', 'exits', 'tenure'] as const;

export const exportPeopleAnalyticsBodySchema = peopleAnalyticsFilterSchema.extend({
  report: z.enum(PEOPLE_ANALYTICS_EXPORTS),
  /** Needed for `headcount`; ignored otherwise. */
  groupBy: headcountGroupingSchema.default('department'),
  /** Needed for `trend`; ignored otherwise. */
  months: z.number().int().min(1).max(36).default(12),
});

/** Returned inline, never stored: it is derived from rows that already exist (as in payroll reports). */
export const peopleAnalyticsExportSchema = z.object({
  fileName: z.string(),
  contentType: z.literal('text/csv'),
  content: z.string(),
  rowCount: z.number().int().nonnegative(),
  generatedAt: instantSchema,
});
export type PeopleAnalyticsExport = z.infer<typeof peopleAnalyticsExportSchema>;

const errors = {
  401: errorBodySchema,
  /** The caller can only read themselves or their team, so there is nothing org-wide to count. */
  403: errorBodySchema,
  /** A filter names a location or department that does not exist, or the date is not real. */
  422: errorBodySchema,
};

export const peopleAnalyticsContract = c.router({
  summary: {
    method: 'GET',
    path: '/people-analytics/summary',
    query: peopleAnalyticsFilterSchema,
    responses: { 200: peopleAnalyticsSummarySchema, ...errors },
    summary: 'Headcount, joiners, leavers, attrition, tenure, notice and probation at a glance',
    metadata: routeMeta('employee:read'),
  },
  headcount: {
    method: 'GET',
    path: '/people-analytics/headcount',
    query: peopleAnalyticsFilterSchema.extend({ groupBy: headcountGroupingSchema.default('department') }),
    responses: { 200: headcountReportSchema, ...errors },
    summary: 'Headcount on a date, split by department, location, grade and so on',
    metadata: routeMeta('employee:read'),
  },
  trend: {
    method: 'GET',
    path: '/people-analytics/trend',
    query: peopleAnalyticsFilterSchema.extend({
      months: z.coerce.number().int().min(1).max(36).default(12),
    }),
    responses: { 200: trendReportSchema, ...errors },
    summary: 'Month by month: opening, joiners, leavers, closing headcount and attrition',
    metadata: routeMeta('employee:read'),
  },
  exits: {
    method: 'GET',
    path: '/people-analytics/exits',
    query: peopleAnalyticsFilterSchema,
    responses: { 200: exitsReportSchema, ...errors },
    summary: 'Who left in the last 12 months: by exit type, reason and length of service',
    metadata: routeMeta('employee:read'),
  },
  tenure: {
    method: 'GET',
    path: '/people-analytics/tenure',
    query: peopleAnalyticsFilterSchema,
    responses: { 200: tenureReportSchema, ...errors },
    summary: 'How long current staff have been with the company, in bands',
    metadata: routeMeta('employee:read'),
  },
  exportReport: {
    method: 'POST',
    path: '/people-analytics/export',
    body: exportPeopleAnalyticsBodySchema,
    responses: { 200: peopleAnalyticsExportSchema, ...errors },
    summary: 'Download one of the reports above as CSV (audited)',
    metadata: routeMeta('employee:export'),
  },
});
